import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Building2, 
  FileText, 
  Mail, 
  MessageSquare,
  TrendingUp,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Properties', 
      value: stats?.properties || 0, 
      icon: Building2, 
      href: '/admin/properties',
      color: 'bg-blue-500'
    },
    { 
      title: 'Blog Posts', 
      value: stats?.blog_posts || 0, 
      icon: FileText, 
      href: '/admin/blog',
      color: 'bg-purple-500'
    },
    { 
      title: 'Subscribers', 
      value: stats?.subscribers || 0, 
      icon: Mail, 
      href: '/admin/subscribers',
      color: 'bg-green-500'
    },
    { 
      title: 'Inquiries', 
      value: stats?.contacts || 0, 
      icon: MessageSquare, 
      href: '/admin/contacts',
      color: 'bg-amber-500',
      badge: stats?.new_contacts > 0 ? `${stats.new_contacts} new` : null
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-1">Welcome to Capitol View Stays CMS</h2>
              <p className="text-slate-300">Manage your properties, blog, and customer inquiries</p>
            </div>
            <TrendingUp className="h-12 w-12 text-amber-400 hidden md:block" />
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.href}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                      {stat.badge && (
                        <span className="inline-block mt-2 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                          {stat.badge}
                        </span>
                      )}
                    </div>
                    <div className={`${stat.color} p-3 rounded-xl`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link 
              to="/admin/properties" 
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <span className="font-medium text-slate-700">Manage Properties</span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </Link>
            <Link 
              to="/admin/blog" 
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <span className="font-medium text-slate-700">Write Blog Post</span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </Link>
            <Link 
              to="/admin/subscribers" 
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <span className="font-medium text-slate-700">View Subscribers</span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </Link>
            <Link 
              to="/admin/seo" 
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <span className="font-medium text-slate-700">SEO Settings</span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
